(function TripSummaryComponentAttachments($, Adzuki)
{
	'use strict';

	// Drakes are held OUTSIDE Vue, keyed by component instance. Anything stored
	// in data() is wrapped in a Vue 3 reactive Proxy, and dragula compares DOM
	// nodes with ===. Through a proxy those identity checks fail, so the drake
	// gets torn down and rebuilt on every call and never settles.
	//
	// A WeakMap rather than one shared variable: this component renders once per
	// component form, and a single variable would let the last one mounted own
	// the only drake.
	const drakes = new WeakMap();

	const component = {
		inject: [
			'getFaIconStyle',
		],
		props: {
			componentId: {
				type    : Number,
				required: false,
			},
			currentAttachments: {
				type    : Array,
				default : [],
				required: false,
			},
			fitBuilderId: {
				type    : Number,
				required: false,
			},
			formId: {
				type    : String,
				required: true,
			},
			formName: {
				type    : String,
				required: true,
			},
		},
		template: '#ts-component-attachments',
		data() {
			return {
				attachments       : this.currentAttachments,
				baseUri           : Adzuki.base_uri,
				showMaxxMediaModal: false,

				// alerts inside of the Media Hub
				showAlert   : false,
				alertTitle  : null,
				alertMessage: null,
				alertType   : null,
			};
		},
		watch: {
			currentAttachments(newAttachments) {
				this.attachments = newAttachments;
			},
			// the list is behind a v-if, so it appears the moment the first
			// attachment lands and needs its drake then, not at mount
			attachments() {
				this.$nextTick(() => this.initAttachmentDrag());
			},
		},
		methods: {
			addAttachmentFromMaxxMedia() {
				this.showMaxxMediaModal = true;
			},
			// TS-314: drag-and-drop reordering. The dragula wiring and its three
			// traps live in Adzuki.AttachmentDrag so the trip-level list (TS-316)
			// shares one copy -- see docs/kb/trip-summary/attachments.md.
			//
			// Called from mounted AND from the attachments watcher: the table sits
			// behind a v-if, so it does not exist until the first attachment lands.
			// Reached by ref, not by querying the root element -- this component has
			// several roots, so it is a fragment and the root is only the first.
			initAttachmentDrag() {
				// The component library, under /TripSummaryComponents/, keeps its
				// attachments in a different table with no sort_order and no reorder
				// endpoint. fitBuilderId is what tells the two contexts apart (the
				// audience method branches on the same flag). No handle there, rather
				// than a handle that silently does nothing.
				if (!this.fitBuilderId) {
					return;
				}

				drakes.set(this, Adzuki.AttachmentDrag.refresh(
					drakes.get(this),
					this.$refs.attachmentList,
					(orderedIds) => {
						this.attachments = orderedIds
							.map((id) => this.attachments.find((a) => String(a.id) === id))
							.filter((attachment) => attachment);

						this.saveAttachmentOrder();
					}
				));
			},
			// TS-415: Move Up / Move Down, the non-drag path to the same save. Offered
			// only where the drag is (canReorder), for the same library reason.
			moveAttachment(index, delta) {
				const target = index + delta;

				if (target < 0 || target >= this.attachments.length) {
					return;
				}

				this.hideTooltips();

				const reordered = this.attachments.slice();

				[reordered[index], reordered[target]] = [reordered[target], reordered[index]];

				this.attachments = reordered;

				this.saveAttachmentOrder();
			},
			// An attachment staged from Maxx Media has a 'mediahub_' string id and does
			// not exist server-side yet. Same test the audience and delete methods use.
			isStagedAttachment(attachment) {
				return (typeof attachment.id === 'string' && attachment.id.startsWith('mediahub_'));
			},
			async saveAttachmentOrder() {
				if (!this.componentId) {
					return;
				}

				// Saved ids are NOT javascript numbers. fetch() hands back raw DB rows
				// and the MySQL driver returns INT columns as strings, so a saved id is
				// "12345". Testing typeof === 'number' here filtered out every real
				// attachment and the reorder silently posted nothing.
				const attachmentIds = this.attachments
					.filter((attachment) => !this.isStagedAttachment(attachment))
					.map((attachment) => attachment.id);

				if (!attachmentIds.length) {
					return;
				}

				try {
					const url      = `/TripSummaries/updateComponentAttachmentOrder/${this.componentId}`;
					const response = await Adzuki.Ajax.post(url, { attachment_ids: attachmentIds.join(',') });

					if (response.error) {
						return Adzuki.TripSummaryError.show(response.error, 'save the attachment order');
					}
				} catch (error) {
					return Adzuki.TripSummaryError.show(error, 'save the attachment order');
				}
			},
			addImageFromMaxxMedia() {
				this.showMaxxMediaModal = true;
			},
			attachmentClass(attachment) {
				const ext = this.getAttachmentExt(attachment);

				switch (ext) {
					case 'pdf':
						return ext;
					case 'doc':
					case 'docx':
						return 'doc';
					case 'csv':
					case 'xls':
					case 'xlsx':
						return 'xls';
					case 'ppt':
					case 'pptx':
						return 'ppt';
					default:
						return 'doc';
				}
			},
			attachmentIcon(attachment) {
				const ext = this.getAttachmentExt(attachment);

				switch (ext) {
					case 'pdf':
						return ext;
					case 'doc':
					case 'docx':
						return 'docs';
					case 'csv':
					case 'xls':
					case 'xlsx':
						return 'excel';
					case 'ppt':
					case 'pptx':
						return 'file_powerpoint';
					default:
						return 'docs';
				}
			},
			closeMaxxMediaModal() {
				this.showMaxxMediaModal = false;
			},
			deleteAttachment(attachment) {
				let url         = '';
				let componentId = '';

				if (this.fitBuilderId) {
					url         = '/TripSummaries/';
					componentId = attachment.fit_builder_component_id;
				} else {
					url         = '/TripSummaryComponents/';
					componentId = attachment.fit_builder_component_library_id;
				}

				url += 'deleteComponentAttachment/';
				url += componentId + '/';
				url += attachment.id;

				this.hideTooltips();

				Adzuki.CustomConfirm.destructive({
					title           : 'Delete “' + Adzuki.CustomConfirm.escape(attachment.name || attachment.filename) + '”?',
					content         : 'It will be removed from this component. This can\'t be undone.',
					confirm_btn_text: 'Delete attachment',
					confirm         : async () => {
						// this attachment was added from the media hub, no need to send request to server
						if (
							typeof attachment.id === 'string'
							&& attachment.id.startsWith('mediahub_')
						) {
							this.attachments = this.attachments.filter(a => a.id !== attachment.id);

							return;
						}

						try {
							const response = await Adzuki.Ajax.get(url);

							if (response.error) {
								return Adzuki.TripSummaryError.show(response.error, 'delete the attachment');
							}

							this.attachments = this.attachments.filter(a => a.id !== attachment.id);

							return Adzuki.Toast('success', 'Attachment deleted.');
						} catch (error) {
							return Adzuki.TripSummaryError.show(error, 'delete the attachment');
						}
					}
				});
			},
			getAttachmentUpdateUrl(attachment) {
				let url = '';

				if (this.fitBuilderId) {
					url = '/TripSummaries/updateComponentAttachmentDetails/';
					url += attachment.fit_builder_component_id + '/';
				} else {
					url = '/TripSummaryComponents/updateComponentAttachmentDetails/';
					url += attachment.fit_builder_component_library_id + '/';
				}

				url += attachment.id;

				return url;
			},
			getAttachmentExt(attachment) {
				if (!attachment.filename) {
					return;
				}

				return attachment.filename.split('.').pop().toLowerCase().trim();
			},
			getDownloadAttachmentUrl(attachment) {
				let url         = this.baseUri;
				let componentId = '';

				// attachment added directly from Maxx Media, download from there
				if (
					typeof attachment.id === 'string'
					&& attachment.id.startsWith('mediahub_')
				) {
					const mediaId = attachment.id.replace('mediahub_', '');
					url += '/Media/downloadMediaItem/' + mediaId;

					return url;
				}

				if (this.fitBuilderId) {
					url += '/TripSummaries/';
					componentId = attachment.fit_builder_component_id;
				} else {
					url += '/TripSummaryComponents/';
					componentId = attachment.fit_builder_component_library_id;
				}

				url += 'downloadComponentAttachment/';
				url += componentId + '/';
				url += attachment.id;

				return url;
			},
			hideTooltips() {
				$('[data-toggle="tooltip"]').tooltip('hide');
			},
			async selectMedia(media) {
				if (!media.media_id) {
					return;
				}

				const mediaIds = [];

				if (Array.isArray(media.media_id)) {
					mediaIds.push(...media.media_id);
				} else {
					mediaIds.push(media.media_id);
				}

				try {
					const response = await Adzuki.Ajax.post('/Media/getMediaItemsById', {
						media_ids: JSON.stringify(mediaIds)
					});

					if (response.status === 'error') {
						return Adzuki.TripSummaryError.show(response.message, 'load the selected media');
					}

					const mediaItems = response;

					for (const mediaItem of mediaItems) {
						const attachment = {
							id             : 'mediahub_' + mediaItem.id,
							audience       : 'client',
							name           : mediaItem.title,
							attachment_path: mediaItem.path,
							filename       : mediaItem.original_file_name,
							description    : mediaItem.description,
						};

						this.attachments.push(attachment);
					}
				} catch (error) {
					return Adzuki.TripSummaryError.show(error, 'load the selected media');
				}
			},
			async updateAttachmentAudience(event, attachment) {
				let url         = '';
				let componentId = '';

				attachment.audience = event.target.checked ? 'client' : 'agent';

				if (this.fitBuilderId) {
					url         = '/TripSummaries/';
					componentId = attachment.fit_builder_component_id;
				} else {
					url         = '/TripSummaryComponents/';
					componentId = attachment.fit_builder_component_library_id;
				}

				url += 'updateComponentAttachmentAudience/';
				url += componentId + '/';
				url += attachment.id + '/';
				url += attachment.audience;

				this.hideTooltips();

				// this attachment was added from the media hub, no need to send request to server
				if (
					typeof attachment.id === 'string'
					&& attachment.id.startsWith('mediahub_')
				) {
					this.attachments = this.attachments.map(a => {
						if (a.id === attachment.id) {
							a.audience = attachment.audience;
						}

						return a;
					});

					return;
				}

				try {
					const response = await Adzuki.Ajax.get(url);

					if (response.error) {
						return Adzuki.TripSummaryError.show(response.error, 'update who can see the attachment');
					}
				} catch (error) {
					return Adzuki.TripSummaryError.show(error, 'update who can see the attachment');
				}
			},
		},
		mounted() {
			this.$nextTick(() => this.initAttachmentDrag());
		},
		unmounted() {
			const drake = drakes.get(this);

			if (drake) {
				drake.destroy();
				drakes.delete(this);
			}
		}
	};

	Adzuki.VueComponents.TripSummaryComponentAttachments = component;
}(jQuery, window.Adzuki || {}));
